import { useState } from 'react';
import { graphql } from 'react-relay';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	PagesDisplayPersistenceOption,
	useHomeViewsUserPreferencesQuery$data,
	useHomeViewsUserPreferencesQuery$variables,
} from './__generated__/useHomeViewsUserPreferencesQuery.graphql.ts';
import type {
	useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation$data,
	useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation$variables,
} from './__generated__/useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation.graphql.ts';
import type { useHomeViewsUserPreferencesFragment$data } from './__generated__/useHomeViewsUserPreferencesFragment.graphql.ts';

const useHomeViewsUserPreferencesQuery = graphql`
	query useHomeViewsUserPreferencesQuery {
		userPreferences {
			homePagesDisplayView
		}
	}
`;

type UseHomeViewsUserPreference = (defaultPreference?: PagesDisplayPersistenceOption) => {
	loading: boolean;
	queryError?: ApolloErrorInterop;
	mutationError?: ApolloErrorInterop;
	appearance: PagesDisplayPersistenceOption;
	updateHomeViewsUserPreference: (appearance: PagesDisplayPersistenceOption) => void;
};

export const useLoggedInHomeViewsUserPreferences: UseHomeViewsUserPreference = (
	defaultPreference = 'CARDS' as PagesDisplayPersistenceOption,
) => {
	const { isLoggedIn } = useSessionData();
	const writeFragmentInterop = useWriteFragmentInterop<
		Partial<useHomeViewsUserPreferencesFragment$data>
	>({
		fragmentSpec: graphql`
			fragment useHomeViewsUserPreferencesFragment on UserPreferences {
				homePagesDisplayView,
				__typename
			}
		`,
	});
	const {
		data,
		loading,
		error: queryError,
	} = useQueryInterop<
		useHomeViewsUserPreferencesQuery$data,
		useHomeViewsUserPreferencesQuery$variables
	>(useHomeViewsUserPreferencesQuery, {
		skip: !isLoggedIn,
	});

	const appearance = data?.userPreferences?.homePagesDisplayView || defaultPreference;
	const [homeViewsUserPreferencesMutation, { error: mutationError }] = useMutationInterop<
		useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation$data,
		useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation$variables
	>(graphql`
		mutation useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation(
			$appearance: PagesDisplayPersistenceOption!
		) {
			updateUserPreferences(userPreferences: { homePagesDisplayView: $appearance }) {
				homePagesDisplayView
			}
		}
	`);
	const updateHomeViewsUserPreference = (appearance: PagesDisplayPersistenceOption) => {
		if (isLoggedIn) {
			void homeViewsUserPreferencesMutation({
				variables: { appearance },
				optimisticResponse: {
					// @ts-ignore __typename does not exist on autogenerated graphql types
					__typename: 'Mutation',
					updateUserPreferences: {
						// @ts-ignore __typename does not exist on autogenerated graphql types
						__typename: 'UserPreferences',
						homePagesDisplayView: appearance,
					},
				},
			}).catch(() => {}); // Ignore the error here since it is handled in useMutation
			// Always write to the cache so the user sees the preference even if there is an error
			if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
				// Use writeFragmentInterop instead of writeQuery
				writeFragmentInterop({
					fragmentId: '$ROOT_QUERY.userPreferences',
					data: {
						homePagesDisplayView: appearance,
						__typename: 'UserPreferences',
					},
				});
			} else {
				writeQueryInterop<useHomeViewsUserPreferencesQuery$data>({
					query: useHomeViewsUserPreferencesQuery,
					data: {
						userPreferences: {
							// @ts-ignore __typename does not exist on autogenerated graphql types
							__typename: 'UserPreferences',
							homePagesDisplayView: appearance,
						},
					},
				});
			}
		}
	};
	return {
		loading,
		queryError,
		mutationError,
		appearance,
		updateHomeViewsUserPreference,
	};
};
export const useAnonymousHomeViewsUserPreferences: UseHomeViewsUserPreference = (
	defaultPreference: PagesDisplayPersistenceOption = 'CARDS' as PagesDisplayPersistenceOption,
) => {
	const [appearance, setAppearance] = useState(defaultPreference);
	return {
		loading: false,
		appearance,
		updateHomeViewsUserPreference: setAppearance,
	};
};
